<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  NButton,
  NCheckbox,
  NEmpty,
  NModal,
  NSpace,
  useDialog,
  useMessage,
} from 'naive-ui'
import {
  getPublishedCyGameListPage,
  getPublishedProjectVersions,
  type PublishedProjectListItem,
  type PublishedProjectVersionItem,
} from '@/features/published-project/api'
import { useAuthStore } from '@/features/auth'
import { sortPublishedVersions } from '@/features/published-project/publishedProjectHelpers'
import { useWorkspaceStore } from '@/store/workspace'
import BrowserUtil from '@/platform/oss/browserUtil'
import { PROJECT_CURRENT_PROJECT_BIN } from '@/features/project-cloud/ossPaths'

type VersionState = {
  loading: boolean
  error: string
  items: PublishedProjectVersionItem[]
}

const props = defineProps<{
  active: boolean
}>()

const emit = defineEmits<{
  loadingChange: [value: boolean]
}>()

const auth = useAuthStore()
const workspace = useWorkspaceStore()
const router = useRouter()
const message = useMessage()
const dialog = useDialog()

const projects = ref<PublishedProjectListItem[]>([])
const versionsByGuid = ref<Record<string, VersionState>>({})
const loading = ref(false)
const openingKey = ref('')
const error = ref('')
const expandedProjectGuids = ref(new Set<string>())
const currentPage = ref(1)
const totalCount = ref(0)
const serverPageSize = ref(0)
const hasAutoLoaded = ref(false)
const overwriteFirstConfirmOpen = ref(false)
const overwriteSecondConfirmOpen = ref(false)
const overwriteConfirmChecked = ref(false)
const pendingOpenRequest = ref<{
  project: PublishedProjectListItem
  version: PublishedProjectVersionItem
} | null>(null)
const overwriteDialogStyle = {
  width: '420px',
  maxWidth: 'calc(100vw - 32px)',
}
let requestToken = 0
let lastSessionId = ''

const hasPrevPage = computed(() => currentPage.value > 1)
const hasNextPage = computed(() => (
  totalCount.value > 0
  && serverPageSize.value > 0
  && currentPage.value * serverPageSize.value < totalCount.value
))

function formatTime(value: string): string {
  const trimmed = String(value || '').trim()
  if (!trimmed) return '-'
  const unix = Number(trimmed)
  if (Number.isFinite(unix) && unix > 0) {
    const ms = unix > 1e12 ? unix : unix * 1000
    const date = new Date(ms)
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('zh-CN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
    }
  }
  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return trimmed
  return new Intl.DateTimeFormat('zh-CN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function formatFontName(value: string): string {
  const fileName = String(value || '')
    .trim()
    .split(/[\\/]/)
    .filter(Boolean)
    .pop() || ''
  return fileName
    .replace(/\.[^.\\/]+$/i, '')
}

function resetProjectListState(): void {
  projects.value = []
  versionsByGuid.value = {}
  expandedProjectGuids.value = new Set()
  error.value = ''
  currentPage.value = 1
  totalCount.value = 0
  serverPageSize.value = 0
}

/** 加载指定页作品列表。 */
async function loadProjectPage(token: number, page: number): Promise<void> {
  if (!auth.sessionId) return
  const result = await getPublishedCyGameListPage(auth.sessionId, page)
  if (token !== requestToken) return
  totalCount.value = result.count
  if (result.items.length > 0) {
    serverPageSize.value = result.items.length
  }
  projects.value = result.items
  versionsByGuid.value = {}
  expandedProjectGuids.value = new Set()
  currentPage.value = page
}

/** 加载作品列表。 */
async function loadProjects(page = 1): Promise<void> {
  if (!auth.loggedIn || !auth.sessionId) return
  const token = ++requestToken
  hasAutoLoaded.value = true
  loading.value = true
  emit('loadingChange', true)
  error.value = ''
  try {
    await loadProjectPage(token, page)
  } catch (err) {
    if (token !== requestToken) return
    error.value = err instanceof Error ? err.message : '已发布作品列表获取失败'
  } finally {
    if (token === requestToken) {
      loading.value = false
      emit('loadingChange', false)
    }
  }
}

defineExpose({
  reload: () => loadProjects(currentPage.value),
})

async function goPrevPage(): Promise<void> {
  if (!hasPrevPage.value || loading.value) return
  await loadProjects(currentPage.value - 1)
}

async function goNextPage(): Promise<void> {
  if (!hasNextPage.value || loading.value) return
  await loadProjects(currentPage.value + 1)
}

async function loadVersions(project: PublishedProjectListItem): Promise<void> {
  const current = versionsByGuid.value[project.guid]
  if (current?.loading || current?.items.length) return
  versionsByGuid.value = {
    ...versionsByGuid.value,
    [project.guid]: { loading: true, error: '', items: [] },
  }
  try {
    const rawVersions = await getPublishedProjectVersions(auth.sessionId, project.gindex)
    const items = sortPublishedVersions(rawVersions)
    versionsByGuid.value = {
      ...versionsByGuid.value,
      [project.guid]: { loading: false, error: '', items },
    }
  } catch (err) {
    versionsByGuid.value = {
      ...versionsByGuid.value,
      [project.guid]: {
        loading: false,
        error: err instanceof Error ? err.message : '历史版本获取失败',
        items: [],
      },
    }
  }
}

function isProjectExpanded(project: PublishedProjectListItem): boolean {
  return expandedProjectGuids.value.has(project.guid)
}

async function toggleProjectExpanded(project: PublishedProjectListItem): Promise<void> {
  const next = new Set(expandedProjectGuids.value)
  if (next.has(project.guid)) {
    next.delete(project.guid)
    expandedProjectGuids.value = next
    return
  }
  next.add(project.guid)
  expandedProjectGuids.value = next
  await loadVersions(project)
}

async function hasCurrentProjectInOss(guid: string): Promise<boolean> {
  if (!auth.uid) return false
  const oss = BrowserUtil.getInstance().connect(auth.uid)
  return await oss.hasObject(`${guid}/${PROJECT_CURRENT_PROJECT_BIN}`)
}

function closeOverwriteConfirm(): void {
  overwriteFirstConfirmOpen.value = false
  overwriteSecondConfirmOpen.value = false
  overwriteConfirmChecked.value = false
  openingKey.value = ''
  pendingOpenRequest.value = null
}

function handleFirstOverwriteConfirm(): void {
  overwriteFirstConfirmOpen.value = false
  overwriteSecondConfirmOpen.value = true
}

async function handleFinalOverwriteConfirm(): Promise<void> {
  const request = pendingOpenRequest.value
  if (!request || !overwriteConfirmChecked.value) return
  overwriteSecondConfirmOpen.value = false
  overwriteConfirmChecked.value = false
  pendingOpenRequest.value = null
  await openVersionDirect(request.project, request.version)
}

async function openVersionDirect(
  project: PublishedProjectListItem,
  version: PublishedProjectVersionItem,
): Promise<void> {
  openingKey.value = `${project.guid}:${version.version}`
  try {
    const result = await workspace.openPublishedProject({
      guid: project.guid,
      projectName: project.projectName,
      version,
    })
    await router.push({ name: 'workspace' })
    if (result.fontFallbackNotice) {
      const notice = result.fontFallbackNotice
      dialog.warning({
        title: '字体缺失',
        content: `当前工程设置字体为【${formatFontName(notice.requestedFontName)}】，资源树中没有该字体，已使用默认字体【${formatFontName(notice.fallbackFontName)}】。`,
        positiveText: '确认',
        onPositiveClick: async () => {
          if (workspace.project) {
            try {
              await workspace.saveProject()
            } catch (error) {
              message.error(error instanceof Error ? error.message : '默认字体保存失败')
              return false
            }
          }
        },
      })
    }
  } catch (err) {
    message.error(err instanceof Error ? err.message : '打开服务器工程失败')
  } finally {
    openingKey.value = ''
  }
}

async function openVersion(project: PublishedProjectListItem, version: PublishedProjectVersionItem): Promise<void> {
  if (!auth.loggedIn) {
    message.warning('请先登录后再打开已发布作品')
    return
  }
  openingKey.value = `${project.guid}:${version.version}`
  try {
    if (await hasCurrentProjectInOss(project.guid)) {
      pendingOpenRequest.value = { project, version }
      overwriteConfirmChecked.value = false
      overwriteFirstConfirmOpen.value = true
      return
    }
    await openVersionDirect(project, version)
  } catch (err) {
    message.error(err instanceof Error ? err.message : '检查我的作品数据失败')
  } finally {
    if (!pendingOpenRequest.value) {
      openingKey.value = ''
    }
  }
}

watch(
  () => [props.active, auth.loggedIn, auth.sessionId] as const,
  ([active, loggedIn, sessionId]) => {
    const nextSessionId = sessionId || ''
    if (!loggedIn || !nextSessionId) {
      requestToken += 1
      lastSessionId = ''
      hasAutoLoaded.value = false
      resetProjectListState()
      return
    }
    if (lastSessionId !== nextSessionId) {
      requestToken += 1
      lastSessionId = nextSessionId
      hasAutoLoaded.value = false
      resetProjectListState()
    }
    if (active && !hasAutoLoaded.value) {
      void loadProjects(1)
    }
  },
  { immediate: true },
)
</script>

<template>
  <section class="published-project-tab">
    <n-empty v-if="!auth.loggedIn" description="登录后可查看已发布作品" />
    <div v-else-if="loading && !projects.length" class="published-project-tab__state">
      正在加载已发布作品…
    </div>
    <div v-else-if="error" class="published-project-tab__state published-project-tab__state--error">
      {{ error }}
    </div>
    <n-empty v-else-if="!projects.length && !totalCount && !hasPrevPage" description="暂无已发布作品" />
    <template v-else>
      <div v-if="projects.length" class="published-project-tab__list">
        <section
          v-for="project in projects"
          :key="project.guid"
          class="published-project-tab__project"
        >
          <button
            type="button"
            class="published-project-tab__project-main"
            @click="toggleProjectExpanded(project)"
          >
            <span class="published-project-tab__project-title-row">
              <span class="published-project-tab__expand-icon" aria-hidden="true">
                {{ isProjectExpanded(project) ? '▾' : '▸' }}
              </span>
              <span class="published-project-tab__project-title">{{ project.projectName }}</span>
            </span>
            <span class="published-project-tab__project-meta">
              <span>{{ formatTime(project.updateTime) }}</span>
            </span>
          </button>
          <div
            v-if="isProjectExpanded(project)"
            class="published-project-tab__versions"
          >
            <template v-if="versionsByGuid[project.guid]?.loading">
              <span class="published-project-tab__muted">正在加载历史版本…</span>
            </template>
            <template v-else-if="versionsByGuid[project.guid]?.error">
              <span class="published-project-tab__error">{{ versionsByGuid[project.guid]?.error }}</span>
            </template>
            <template v-else-if="versionsByGuid[project.guid]?.items.length">
              <button
                v-for="version in versionsByGuid[project.guid]?.items"
                :key="version.version"
                type="button"
                class="published-project-tab__version"
                :disabled="openingKey === `${project.guid}:${version.version}`"
                @click.stop="openVersion(project, version)"
              >
                <span class="published-project-tab__version-id">版本 {{ version.version }}</span>
                <span class="published-project-tab__version-time">{{ formatTime(version.createTime) }}</span>
                <span class="published-project-tab__version-intro">{{ version.updateIntro || '无更新简介' }}</span>
              </button>
            </template>
            <span
              v-else-if="versionsByGuid[project.guid] && !versionsByGuid[project.guid]?.items.length"
              class="published-project-tab__muted"
            >
              暂无历史版本
            </span>
            <span v-else class="published-project-tab__muted">正在准备历史版本…</span>
          </div>
        </section>
      </div>
      <div v-if="totalCount > 0 || hasPrevPage" class="published-project-tab__pager">
        <div class="published-project-tab__pager-actions">
          <n-button
            size="tiny"
            quaternary
            :disabled="!hasPrevPage || loading"
            @click="goPrevPage"
          >
            上一页
          </n-button>
          <span class="published-project-tab__muted">第 {{ currentPage }} 页</span>
          <n-button
            size="tiny"
            quaternary
            :disabled="!hasNextPage || loading"
            @click="goNextPage"
          >
            下一页
          </n-button>
        </div>
      </div>
    </template>
    <n-modal
      v-model:show="overwriteFirstConfirmOpen"
      preset="card"
      :style="overwriteDialogStyle"
      :mask-closable="false"
    >
      <template #header>
        <span class="published-project-tab__danger-title">危险提示</span>
      </template>
      <div class="published-project-tab__danger-text">
        【我的作品】列表中已有该作品，打开会覆盖作品数据
      </div>
      <template #footer>
        <n-space justify="end">
          <n-button @click="closeOverwriteConfirm">取消</n-button>
          <n-button type="error" @click="handleFirstOverwriteConfirm">确认</n-button>
        </n-space>
      </template>
    </n-modal>
    <n-modal
      v-model:show="overwriteSecondConfirmOpen"
      preset="card"
      :style="overwriteDialogStyle"
      :mask-closable="false"
    >
      <template #header>
        <span class="published-project-tab__danger-title">再次确认</span>
      </template>
      <div class="published-project-tab__danger-text">
        此操作覆盖数据无法恢复，建议先发布！
      </div>
      <n-checkbox v-model:checked="overwriteConfirmChecked" class="published-project-tab__danger-check">
        我确认覆盖
      </n-checkbox>
      <template #footer>
        <n-space justify="end">
          <n-button @click="closeOverwriteConfirm">取消</n-button>
          <n-button
            type="error"
            :disabled="!overwriteConfirmChecked"
            @click="handleFinalOverwriteConfirm"
          >
            确认
          </n-button>
        </n-space>
      </template>
    </n-modal>
  </section>
</template>

<style scoped>
.published-project-tab {
  width: 100%;
}

.published-project-tab__list {
  display: flex;
  flex-direction: column;
  background: var(--theme-bg-surface);
}

.published-project-tab__project {
  border-bottom: 1px solid var(--theme-editor-divider);
}

.published-project-tab__project-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-height: 72px;
  padding: 14px 12px 12px;
  font: inherit;
  text-align: left;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-surface);
  border: 0;
  cursor: pointer;
}

.published-project-tab__project-main:hover {
  background: var(--theme-bg-hover);
}

.published-project-tab__project-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.published-project-tab__expand-icon {
  flex: 0 0 auto;
  width: 14px;
  color: var(--theme-text-muted);
  font-size: 12px;
  line-height: 1;
}

.published-project-tab__project-title {
  min-width: 0;
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  color: var(--theme-text-heading);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.published-project-tab__project-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: var(--theme-text-muted);
}
.published-project-tab__state {
  padding: 10px 0;
  font-size: 12px;
  color: var(--theme-text-very-muted);
}

.published-project-tab__state--error,
.published-project-tab__error {
  color: var(--theme-error);
}

.published-project-tab__versions {
  display: flex;
  flex-direction: column;
  padding: 0 12px 10px;
  background: var(--theme-bg-surface);
}

.published-project-tab__version {
  display: grid;
  grid-template-columns: 86px 150px minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 34px;
  padding: 8px 0;
  font: inherit;
  font-size: 12px;
  color: var(--theme-text-secondary);
  text-align: left;
  background: var(--theme-bg-surface);
  border: 0;
  border-top: 1px solid var(--theme-editor-divider);
  cursor: pointer;
}

.published-project-tab__version:hover {
  background: var(--theme-bg-hover);
}

.published-project-tab__version:disabled {
  cursor: wait;
}

.published-project-tab__version-id {
  font-weight: 600;
  color: var(--theme-text-primary);
}

.published-project-tab__version-time {
  color: var(--theme-text-muted);
}

.published-project-tab__version-intro {
  min-width: 0;
  overflow: hidden;
  color: var(--theme-text-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.published-project-tab__muted {
  font-size: 12px;
  color: var(--theme-text-very-muted);
}

.published-project-tab__pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

.published-project-tab__pager-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.published-project-tab__danger-text {
  font-size: 14px;
  color: var(--theme-text-primary);
  line-height: 1.7;
}

.published-project-tab__danger-check {
  margin-top: 14px;
}

.published-project-tab__danger-title {
  font-weight: 600;
  color: var(--theme-error);
}
</style>
